import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { uploadVideoChunked } from "@/lib/uploadVideo";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import EmojiPicker from "emoji-picker-react";
import TierPortal from "@/components/TierPortal";
import MentionTextarea from "@/components/MentionTextarea";
import {
    Image as ImageIcon, Clapperboard, BarChart2, Smile, CalendarClock, X, Plus, Video, Loader2, Play,
} from "lucide-react";
import GifPicker from "@/components/GifPicker";
import AuthorThankTip from "@/components/AuthorThankTip";

const MAX_IMAGES = 4;
const MAX_VIDEOS = 3;

/**
 * X-style inline post composer (Layout 1). Supports photos (≤4), a GIF, a poll,
 * emojis, and scheduling. Calls `onSubmit(payload)` and resets on success.
 */
export default function PostComposer({ onSubmit, accent = "#ff4500", placeholder = "What's happening?", testidPrefix = "composer", showSubject = false, draftKey = null }) {
    const { user } = useAuth();
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState(() => (draftKey ? (localStorage.getItem(draftKey) || "") : ""));
    const [images, setImages] = useState([]);      // [url]
    const [videos, setVideos] = useState([]);       // [{url, poster}]
    const [gif, setGif] = useState(null);           // {preview_url, full_url}
    const [poll, setPoll] = useState(null);         // {options:[str], duration_days}
    const [scheduledFor, setScheduledFor] = useState("");
    const [showGif, setShowGif] = useState(false);
    const [showEmoji, setShowEmoji] = useState(false);
    const [showSchedule, setShowSchedule] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [videoUploading, setVideoUploading] = useState(false);
    const [videoPct, setVideoPct] = useState(0);
    const [posting, setPosting] = useState(false);
    const fileRef = useRef(null);
    const videoRef = useRef(null);

    // Persist an unfinished body to localStorage so a stray collapse doesn't lose it.
    useEffect(() => {
        if (!draftKey) return;
        if (body.trim()) localStorage.setItem(draftKey, body);
        else localStorage.removeItem(draftKey);
    }, [draftKey, body]);

    const canPost = !!(body.trim() || images.length || videos.length || gif || (poll && poll.options.filter((o) => o.trim()).length >= 2));

    const pickImages = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const room = MAX_IMAGES - images.length;
        if (room <= 0) return toast.error(`Up to ${MAX_IMAGES} photos`);
        setUploading(true);
        try {
            const uploaded = [];
            for (const f of files.slice(0, room)) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
                uploaded.push(data.url);
            }
            setImages((prev) => [...prev, ...uploaded]);
            setGif(null); // photos and GIF are mutually exclusive
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const videoDuration = (file) => new Promise((resolve) => {
        try {
            const url = URL.createObjectURL(file);
            const v = document.createElement("video");
            v.preload = "metadata";
            v.onloadedmetadata = () => { URL.revokeObjectURL(url); resolve(v.duration || 0); };
            v.onerror = () => { URL.revokeObjectURL(url); resolve(0); };
            v.src = url;
        } catch { resolve(0); }
    });

    const pickVideos = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = "";
        if (!files.length) return;
        const room = MAX_VIDEOS - videos.length;
        if (room <= 0) return toast.error(`Up to ${MAX_VIDEOS} videos`);
        setVideoUploading(true);
        setVideoPct(0);
        try {
            const uploaded = [];
            for (const f of files.slice(0, room)) {
                const dur = await videoDuration(f);
                if (dur && dur > 31) { toast.error(`"${f.name}" is longer than 30 seconds — trim it first`); continue; }
                const data = await uploadVideoChunked(f, setVideoPct);
                uploaded.push({ url: data.url, poster: data.poster });
            }
            if (uploaded.length) { setVideos((prev) => [...prev, ...uploaded]); setGif(null); }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Video upload failed");
        } finally { setVideoUploading(false); setVideoPct(0); }
    };

    const openPoll = () => {
        setPoll((p) => (p ? null : { options: ["", ""], duration_days: 1 }));
    };
    const setOpt = (i, v) => setPoll((p) => ({ ...p, options: p.options.map((o, idx) => (idx === i ? v : o)) }));
    const addOpt = () => setPoll((p) => (p.options.length >= 4 ? p : { ...p, options: [...p.options, ""] }));
    const removeOpt = (i) => setPoll((p) => ({ ...p, options: p.options.filter((_, idx) => idx !== i) }));

    const reset = () => {
        setSubject(""); setBody(""); setImages([]); setVideos([]); setGif(null); setPoll(null); setScheduledFor("");
        setShowGif(false); setShowEmoji(false); setShowSchedule(false);
        if (draftKey) localStorage.removeItem(draftKey);
    };

    const submit = async () => {
        if (!canPost || posting) return;
        let scheduledIso = null;
        if (scheduledFor) {
            const d = new Date(scheduledFor);
            if (isNaN(d.getTime())) return toast.error("Invalid schedule time");
            if (d.getTime() <= Date.now()) return toast.error("Pick a future time");
            scheduledIso = d.toISOString();
        }
        const payload = {
            subject: showSubject ? subject.trim() : "",
            body: body.trim(),
            images,
            videos,
            gif: gif || null,
            poll: poll && poll.options.filter((o) => o.trim()).length >= 2
                ? { options: poll.options.filter((o) => o.trim()), duration_days: poll.duration_days }
                : null,
            scheduled_for: scheduledIso,
        };
        setPosting(true);
        try {
            await onSubmit(payload);
            reset();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not post");
        } finally { setPosting(false); }
    };

    if (!user) {
        return (
            <div className="border border-[#2a2a2a] bg-[#141414] p-4 text-sm text-[#a0a0a0]" data-testid={`${testidPrefix}-login-prompt`}>
                Log in to post.
            </div>
        );
    }

    const initial = (user.username || user.name || "?")[0]?.toUpperCase();

    return (
        <div className="border border-[#2a2a2a] bg-[#141414] p-4" data-testid={testidPrefix}>
            {/* Author header: avatar + username + thank/tip (matches the creator-credit row) */}
            <div className="flex items-center gap-2 mb-3">
                <TierPortal count={user.thanks_count || 0} size={30} ringW={2} gap={2}>
                    {user.photo ? (
                        <img src={user.photo} alt="" className="h-full w-full object-cover" />
                    ) : (
                        <span className="flex h-full w-full items-center justify-center bg-[#1a1a1a] text-xs" style={{ color: accent }}>{initial}</span>
                    )}
                </TierPortal>
                <span className="font-bold text-sm text-white" data-testid={`${testidPrefix}-username`}>{user.username || user.name}</span>
                <AuthorThankTip userId={user.id} username={user.username || user.name} tipLinks={user.tip_links} thanksCount={user.thanks_count || 0} contextType="profile" contextId={user.id} />
            </div>
            <div className="min-w-0">
                    {showSubject && (
                        <>
                        <input
                            data-testid={`${testidPrefix}-subject`}
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder="Title (optional)"
                            maxLength={200}
                            className="w-full mb-1 bg-[#0f0f0f] border border-[#2a2a2a] px-3 py-2.5 text-[15px] text-white outline-none focus:border-[#3a3a3a] placeholder:text-[#666]"
                        />
                        <p className="mb-2 text-[11px] text-[#666]" data-testid={`${testidPrefix}-subject-hint`}>A title is optional — a post with just a message is perfectly fine.</p>
                        </>
                    )}
                    <MentionTextarea
                        testid={`${testidPrefix}-body`}
                        value={body}
                        onChange={setBody}
                        placeholder={placeholder}
                        rows={2}
                        maxLength={5000}
                        className="w-full resize-none bg-transparent text-[17px] text-white outline-none placeholder:text-[#666]"
                    />

                    {/* Image previews */}
                    {images.length > 0 && (
                        <div className="grid grid-cols-2 gap-1.5 mt-1" data-testid={`${testidPrefix}-images`}>
                            {images.map((url, i) => (
                                <div key={i} className="relative">
                                    <img src={url} alt="" className="h-28 w-full object-cover rounded-lg" />
                                    <button type="button" data-testid={`${testidPrefix}-remove-image-${i}`} onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))}
                                        className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black">
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Video previews */}
                    {(videos.length > 0 || videoUploading) && (
                        <div className="grid grid-cols-3 gap-1.5 mt-2" data-testid={`${testidPrefix}-videos`}>
                            {videos.map((v, i) => (
                                <div key={i} className="relative aspect-video">
                                    {v.poster
                                        ? <img src={v.poster} alt="" className="h-full w-full object-cover rounded-lg bg-black" />
                                        : <div className="h-full w-full rounded-lg bg-black" />}
                                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60"><Play className="h-4 w-4 text-white" fill="white" /></span>
                                    </span>
                                    <button type="button" data-testid={`${testidPrefix}-remove-video-${i}`} onClick={() => setVideos((p) => p.filter((_, idx) => idx !== i))}
                                        className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black">
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                            {videoUploading && (
                                <div className="aspect-video rounded-lg bg-[#0f0f0f] border border-[#2a2a2a] flex flex-col items-center justify-center gap-1 text-[#a0a0a0]" data-testid={`${testidPrefix}-video-processing`}>
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                    <span className="text-[10px] uppercase tracking-wider">{videoPct > 0 && videoPct < 100 ? `Uploading ${videoPct}%` : "Processing…"}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* GIF preview */}
                    {gif && (
                        <div className="relative w-fit mt-2">
                            <img src={gif.full_url} alt="GIF" className="max-h-56 rounded-lg" data-testid={`${testidPrefix}-gif-preview`} />
                            <button type="button" data-testid={`${testidPrefix}-remove-gif`} onClick={() => setGif(null)}
                                className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-white hover:bg-black">
                                <X className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}

                    {/* Poll builder */}
                    {poll && (
                        <div className="mt-3 space-y-2 border border-[#2a2a2a] p-3" data-testid={`${testidPrefix}-poll`}>
                            {poll.options.map((o, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input
                                        data-testid={`${testidPrefix}-poll-option-${i}`}
                                        value={o}
                                        maxLength={40}
                                        onChange={(e) => setOpt(i, e.target.value)}
                                        placeholder={`Choice ${i + 1}`}
                                        className="flex-1 h-9 bg-[#0f0f0f] border border-[#2a2a2a] px-3 text-sm text-white outline-none focus:border-[#3a3a3a]"
                                    />
                                    {poll.options.length > 2 && (
                                        <button type="button" data-testid={`${testidPrefix}-poll-remove-${i}`} onClick={() => removeOpt(i)} className="text-[#666] hover:text-red-500"><X className="h-4 w-4" /></button>
                                    )}
                                </div>
                            ))}
                            <div className="flex items-center justify-between">
                                {poll.options.length < 4 ? (
                                    <button type="button" data-testid={`${testidPrefix}-poll-add`} onClick={addOpt} className="inline-flex items-center gap-1 text-xs" style={{ color: accent }}><Plus className="h-3.5 w-3.5" /> Add choice</button>
                                ) : <span />}
                                <label className="text-xs text-[#a0a0a0] flex items-center gap-2">
                                    Length
                                    <select data-testid={`${testidPrefix}-poll-duration`} value={poll.duration_days} onChange={(e) => setPoll((p) => ({ ...p, duration_days: Number(e.target.value) }))} className="bg-[#0f0f0f] border border-[#2a2a2a] px-2 py-1 text-white outline-none">
                                        {[1, 3, 7].map((d) => <option key={d} value={d}>{d} day{d > 1 ? "s" : ""}</option>)}
                                    </select>
                                </label>
                                <button type="button" data-testid={`${testidPrefix}-poll-close`} onClick={() => setPoll(null)} className="text-xs text-[#666] hover:text-white">Remove poll</button>
                            </div>
                        </div>
                    )}

                    {/* Schedule chip */}
                    {scheduledFor && (
                        <div className="mt-2 inline-flex items-center gap-2 text-xs text-[#22c55e] border border-[#22c55e]/40 px-2 py-1 rounded-full" data-testid={`${testidPrefix}-schedule-chip`}>
                            <CalendarClock className="h-3.5 w-3.5" /> Sends {new Date(scheduledFor).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                            <button type="button" onClick={() => setScheduledFor("")} className="hover:text-white"><X className="h-3 w-3" /></button>
                        </div>
                    )}

                    <div className="mt-2 border-t border-[#2a2a2a]" />

                    {/* Toolbar */}
                    <div className="mt-2 flex items-center gap-1 relative">
                        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickImages} data-testid={`${testidPrefix}-file-input`} />
                        <input ref={videoRef} type="file" accept="video/*" multiple hidden onChange={pickVideos} data-testid={`${testidPrefix}-video-input`} />
                        <ToolBtn testid={`${testidPrefix}-tool-image`} title="Photos" onClick={() => fileRef.current?.click()} disabled={!!gif || uploading} accent={accent}><ImageIcon className="h-[18px] w-[18px]" /></ToolBtn>
                        <ToolBtn testid={`${testidPrefix}-tool-video`} title="Video (30s max)" onClick={() => videoRef.current?.click()} disabled={!!gif || videoUploading || videos.length >= MAX_VIDEOS} accent={accent}>{videoUploading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Video className="h-[18px] w-[18px]" />}</ToolBtn>
                        <ToolBtn testid={`${testidPrefix}-tool-gif`} title="GIF" onClick={() => { setShowGif((s) => !s); setShowEmoji(false); }} disabled={images.length > 0 || videos.length > 0} accent={accent}><Clapperboard className="h-[18px] w-[18px]" /></ToolBtn>
                        <ToolBtn testid={`${testidPrefix}-tool-poll`} title="Poll" onClick={openPoll} active={!!poll} accent={accent}><BarChart2 className="h-[18px] w-[18px]" /></ToolBtn>
                        <ToolBtn testid={`${testidPrefix}-tool-emoji`} title="Emoji" onClick={() => { setShowEmoji((s) => !s); setShowGif(false); }} active={showEmoji} accent={accent}><Smile className="h-[18px] w-[18px]" /></ToolBtn>
                        <ToolBtn testid={`${testidPrefix}-tool-schedule`} title="Schedule" onClick={() => setShowSchedule((s) => !s)} active={!!scheduledFor} accent={accent}><CalendarClock className="h-[18px] w-[18px]" /></ToolBtn>

                        <button
                            type="button"
                            data-testid={`${testidPrefix}-submit`}
                            onClick={submit}
                            disabled={!canPost || posting || uploading || videoUploading}
                            className="ml-auto rounded-full px-5 py-1.5 text-sm font-bold text-white transition-opacity disabled:opacity-40 hover:opacity-90"
                            style={{ backgroundColor: accent }}
                        >
                            {posting ? "…" : scheduledFor ? "Schedule" : "Post"}
                        </button>

                        {showGif && <GifPicker accent={accent} onClose={() => setShowGif(false)} onChoose={(g) => { setGif(g); setImages([]); setVideos([]); setShowGif(false); }} />}
                        {showEmoji && (
                            <div className="absolute z-30 top-10 left-0" data-testid={`${testidPrefix}-emoji-picker`}>
                                <EmojiPicker theme="dark" width={300} height={360} onEmojiClick={(ed) => setBody((b) => b + ed.emoji)} />
                            </div>
                        )}
                    </div>

                    {/* Schedule picker */}
                    {showSchedule && (
                        <div className="mt-2 flex items-center gap-2" data-testid={`${testidPrefix}-schedule-picker`}>
                            <input
                                type="datetime-local"
                                data-testid={`${testidPrefix}-schedule-input`}
                                value={scheduledFor}
                                min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
                                onChange={(e) => setScheduledFor(e.target.value)}
                                className="bg-[#0f0f0f] border border-[#2a2a2a] px-3 py-1.5 text-sm text-white outline-none"
                            />
                            <button type="button" onClick={() => setShowSchedule(false)} className="text-xs text-[#a0a0a0] hover:text-white">Done</button>
                        </div>
                    )}
                </div>
        </div>
    );
}

function ToolBtn({ children, onClick, title, disabled, active, testid, accent }) {
    return (
        <button
            type="button"
            title={title}
            data-testid={testid}
            onClick={onClick}
            disabled={disabled}
            className="flex h-8 w-8 items-center justify-center rounded-full transition-colors disabled:opacity-30 hover:bg-white/5"
            style={{ color: active ? accent : accent }}
        >
            {children}
        </button>
    );
}
